import React from "react";

import { formatCurrency, formatToTwoDecimalPlaces, monthNames } from "../utils";

const EMPTY_CLASSIFICATION = "N/A";
const ARCHIVED_CLASSIFICATION = "(Archivado)";

export const processData = ({
	dataToProcess = [], 
	exchangeRate = 7.71,
	isUSDActive = false,
	basePoints: basePointsToCalculate = {},  
	bankAccounts: bankAccountsForRecalculate = [],
	currentYear = 0,
	originalCurrency = "",
	basePointsAllreadyConverted = false,
	classifications: classificationToCheck = [],
}) => {
	if(dataToProcess.length === 0){
		return {
			rows: [],
			detectedInitialAmountBankAccounts: [],
			grandTotal: {
				monthly: Array(12).fill(0),
				total: 0,
			},
			initialAmountTotalRow: Array(12).fill(0),
			finalAmountTotalRow: Array(12).fill(0),
		};
	}
	
	const firstBasePoints = getFirstBasePoints(bankAccountsForRecalculate);
	
	const months = monthNames;
	const hierarchy = {};
	
	// Crear un mapa para acceder rápidamente a las clasificaciones por ID
	const classificationsMap = {};
	const classificationPathMap = {}; // Mapa para guardar rutas de clasificación
	
	// Función recursiva para construir el mapa de clasificaciones
	const buildClassificationsMap = (classifications) => {
		classifications.forEach(classification => {
			// Guardar en el mapa por ID
			classificationsMap[classification.id] = classification;
			
			// Guardar la ruta de clasificación para un acceso rápido
			if (classification.classification) {
				classificationPathMap[classification.classification] = classification;
			}
			
			// Procesar hijos si existen
			if (classification.children && classification.children.length > 0) {
				buildClassificationsMap(classification.children);
			}
		});
	};

	buildClassificationsMap(classificationToCheck);
	
	const getClassificationPathById = (classificationId) => {    
		if (!classificationId || classificationId.trim() === "") return [EMPTY_CLASSIFICATION]; // Si no hay ID o está vacío, devolver N/A
				
		const foundClassification = classificationsMap[classificationId]; // Buscar la clasificación por ID
				
		if (!foundClassification) return [EMPTY_CLASSIFICATION]; // Si no se encuentra, devolver N/A

		const { classification } = foundClassification;
				
		const classificationPath = classification || EMPTY_CLASSIFICATION; // Usar la propiedad 'classification' de la clasificación encontrada
				
		return classificationPath.trim() === "" ? [EMPTY_CLASSIFICATION] : classificationPath.split("-"); // Dividir por guiones para mantener la estructura jerárquica
	};
  
	const addToHierarchy = (hierarchy, classificationId, monthIndex, amount, currency) => {
		let currentLevel = hierarchy;
		
		// Obtener la ruta de clasificación a partir del ID
		const classificationParts = getClassificationPathById(classificationId);

		// Recorremos cada parte de la ruta de clasificación
		classificationParts.forEach((classificationName, idx) => {
			if (!currentLevel[classificationName]) {
				// Crear el objeto si no existe
				const isLeaf = idx === classificationParts.length - 1;
				const fullPath = classificationParts.slice(0, idx + 1).join("-");
				
				const classificationInfo = classificationPathMap[fullPath] || {};
				
				currentLevel[classificationName] = {
					name: classificationName,
					total: 0,
					monthly: Array(12).fill(0),
					children: {},
					// Guardar información completa de clasificación para ordenar correctamente
					classificationId: isLeaf ? classificationId : classificationInfo.id || null,
					depth: idx,
					sort_order: classificationInfo.sort_order !== undefined ? classificationInfo.sort_order : 999,
					fullPath: fullPath
				};
			}

			// Si es el último nivel, añadir el monto
			if (idx === classificationParts.length - 1) {
				let amountConverted = amount;
				if(isUSDActive){
					amountConverted = currency === "GTQ" ? amount / exchangeRate : amount;
				} else {
					amountConverted = currency === "USD" ? amount * exchangeRate : amount;
				}
				
				currentLevel[classificationName].monthly[monthIndex] += formatToTwoDecimalPlaces(amountConverted);
				currentLevel[classificationName].total += formatToTwoDecimalPlaces(amountConverted);
			}

			// Avanzar al siguiente nivel
			currentLevel = currentLevel[classificationName].children;
		});
	};
	
	// Procesar los datos
	dataToProcess.forEach(({ month, classification_id, total_amount, currency }) => {
		const dateSlipted = month.split("-");
		const monthNumber = dateSlipted[1];
		const monthIndex = parseInt(monthNumber, 10) - 1;
		
		// Usar classification_id en lugar de classification
		addToHierarchy(hierarchy, classification_id, monthIndex, total_amount, currency);
	});

	// Función mejorada para ordenar correctamente según la jerarquía y sort_order
	const buildRows = (hierarchy, depth = 0, parentPath = "") => {
		// Obtener valores y prepararlos para ordenar
		const values = Object.values(hierarchy);
		
		// Ordenar los valores según la jerarquía y sort_order
		const sortedValues = values.sort((a, b) => {
			// Si uno es N/A o Clasificación Archivada, colocarlo al final
			if (a.name === EMPTY_CLASSIFICATION && b.name !== EMPTY_CLASSIFICATION) return 1;
			if (a.name !== EMPTY_CLASSIFICATION && b.name === EMPTY_CLASSIFICATION) return -1;
			
			// Si ambos tienen sort_order, usarlo para ordenar
			if (a.sort_order !== undefined && b.sort_order !== undefined) {
				return a.sort_order - b.sort_order;
			}
			
			// Si solo uno tiene sort_order, priorizarlo
			if (a.sort_order !== undefined) return -1;
			if (b.sort_order !== undefined) return 1;
			
			// Si nada más, ordenar por nombre
			return a.name.localeCompare(b.name);
		});
		
		// Mapear los valores ordenados a filas
		return sortedValues.map((item, index) => {      
			const currentPath = `${parentPath ? parentPath + "-" : ""}${index}`;
			const uniqueKey = `${depth}-${currentPath}-${item.name}`;
			
			const originalClassification = classificationsMap[item.classificationId];
			const isArchived = originalClassification?.archived;

			const displayName = isArchived ? `${item.name} ${ARCHIVED_CLASSIFICATION}` : item.name;
			
			// Construir la fila con todos los datos necesarios
			return {
				key: uniqueKey,
				name: displayName,
				total: item.total,
				depth: item.depth || depth,
				...Object.fromEntries(
					months.map((month, i) => [month, item.monthly[i] || 0])
				),
				// Llamada recursiva para procesar los hijos
				children: buildRows(
					item.children, 
					(item.depth || depth) + 1, 
					currentPath
				),
			};
		});
	};

	// Generar las filas ordenadas
	const rows = buildRows(hierarchy);
	
	const grandTotal = processMonthlyData(rows);

	// Función para actualizar los totales basados en los hijos
	const updateRowsWithChildrenSum = (rows) => {
		return rows.map((row) => {
			if (row.children && row.children.length > 0) {
				row.children = updateRowsWithChildrenSum(row.children);

				monthNames.forEach((month) => {
					row[month] += row.children.reduce((sum, child) => sum + (child[month] || 0), 0);
				});

				row.total += row.children.reduce((sum, child) => sum + child.total, 0);
			}
			return row;
		});
	};

	const updatedRows = updateRowsWithChildrenSum(rows);

	// Inicializar arrays para almacenar los valores
	let initialAmountTotalRow = Array(12).fill(0);
	let finalAmountTotalRow = Array(12).fill(0);
	const detectedInitialAmountBankAccounts = [];

	// CORRECCIÓN: Obtener el último mes-año disponible en los base points
	const sortedBasePoints = Object.keys(basePointsToCalculate)
		.sort((a, b) => new Date(a) - new Date(b));

	// Determinar el punto base inicial adecuado para este año
	let initialBasePoint = null;
	
	// Si estamos procesando datos del año actual, tomamos el punto base más reciente del año anterior
	// o el punto base más antiguo del año actual si no hay ninguno del año anterior
	const previousYearBasePoints = sortedBasePoints.filter(bp => parseInt(bp.split("-")[0]) < currentYear);	
	
	if (previousYearBasePoints.length > 0) {
		// Usar el punto base más reciente del año anterior
		initialBasePoint = previousYearBasePoints[previousYearBasePoints.length - 1];
	} 	

	// Obtener el valor del punto base inicial
	let initialBasePointValue = 0;
	if (initialBasePoint && basePointsToCalculate[initialBasePoint]) {
		initialBasePointValue = basePointsToCalculate[initialBasePoint];
		
		// Convertir si es necesario
		if (!basePointsAllreadyConverted) {
			if (isUSDActive && originalCurrency === "GTQ") {
				initialBasePointValue = initialBasePointValue / exchangeRate;
			} else if (!isUSDActive && originalCurrency === "USD") {
				initialBasePointValue = initialBasePointValue * exchangeRate;
			}
		}
	}
		
	// ? => Obtener los registros de las cuentas bancarias que empezaron a tener transacciones en el año seleccionado.
	for (let i = 0; i < 12; i++) {						
		const currentMonth = i + 1;
		const currentMonthFormatted = String(currentMonth).padStart(2, "0");
		
		const monthKey = `${currentYear}-${currentMonthFormatted}`;
		if (firstBasePoints[monthKey]) {
			firstBasePoints[monthKey].forEach(({ account_number, bank, firstBasePointValue, currency }) => {
				let existingEntry = detectedInitialAmountBankAccounts.find(entry => entry.name === `${bank}: ${account_number}`);
				
				if (!existingEntry) {
					existingEntry = {
						name: `${bank}: ${account_number}`,
						monthly: Array(12).fill(0),
						total: 0.00,
					};
					detectedInitialAmountBankAccounts.push(existingEntry);
				}

				let convertedBasePoint = firstBasePointValue;
				if (isUSDActive && currency === "GTQ") {
					convertedBasePoint = firstBasePointValue / exchangeRate;
				} else if (!isUSDActive && currency === "USD") {
					convertedBasePoint = firstBasePointValue * exchangeRate;
				}

				const finalAmountToSet = formatToTwoDecimalPlaces(convertedBasePoint);
				existingEntry.monthly[i] = finalAmountToSet;
				existingEntry.total += finalAmountToSet;
			});
		}			
	}
	
	const monthlyTotals = Array(12).fill(0);
	
	// ? Obtener el total de los saldos iniciales por mes de cada una de las cuentas bancarias.
	detectedInitialAmountBankAccounts.forEach(({ monthly }) => {
		monthly.forEach((value, index) => {
			monthlyTotals[index] += value;
		});
	});

	// ? Sumar entre los totales de los saldos iniciales de las cuentas bancarias y el total de las transacciones encontradas.
	const { monthly } = grandTotal;
	const combinedMonthly = monthlyTotals.map((val, index) => val + monthly[index]);
	const combinedTotal = combinedMonthly.reduce((sum, val) => sum + val, 0);
	
	// ? Realizar la operación con el resultado combinado y realizar sus debidas sumas y restas
	for (let i = 0; i < 12; i++) {
		if (i === 0) {
			initialAmountTotalRow[i] = initialBasePointValue;
		} else {
			initialAmountTotalRow[i] = finalAmountTotalRow[i - 1];
		}

		finalAmountTotalRow[i] = initialAmountTotalRow[i] + combinedMonthly[i];
	}
	
	return {
		rows: updatedRows,
		detectedInitialAmountBankAccounts,
		grandTotal: {
			monthly: combinedMonthly,
			total: combinedTotal,
		},
		initialAmountTotalRow,
		finalAmountTotalRow,
	};
};

// ! ============================================================================================

export const renderColumns = (currency) => {
	return [
		{
			title: "Clasificación",
			dataIndex: "name",
			key: "name",
			fixed: "left",
			width: 250,
			render: (text, record) => {
				return (
					<span style={{ fontWeight: getFontWeight(record.depth), paddingLeft: `${record.depth * 20}px` }}>
						{text}
					</span>
				);
			},
		},
		...monthNames.map((month) => ({
			title: <span style={{ whiteSpace: "nowrap" }}>{month.slice(0, 3)}</span>,
			dataIndex: month,
			key: month,
			align: "right",
			width: 110,
			render: (value, record) => {
				return (<span style={{ fontWeight: getFontWeight(record.depth) }}>{value !== 0 ? `${currency} ${formatCurrency(value)}` : "-"}</span>);
			},
		})),
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			align: "right",
			fixed: "right",
			render: (value, record) => {
				return (<span style={{ fontWeight: getFontWeight(record.depth) }}>{value !== 0 ? `${currency} ${formatCurrency(value)}` : "0"}</span>);
			},
		},
	];
};

// ! ============================================================================================

export const getAllKeys = (data) => {
	let keys = [];
	data.forEach(item => {
		keys.push(item.key);
		if (item.children) {
			keys = [...keys, ...getAllKeys(item.children)];
		}
	});
	return keys;
};

// ! ============================================================================================

const getFirstBasePoints = (bankAccounts) => {
	return bankAccounts.reduce((acc, { account_number, bank, currency, basePoints }) => {
		const sortedBasePoints = Object.entries(basePoints)
			.sort((a, b) => new Date(a[0]) - new Date(b[0])); // Ordena por fecha

		if (sortedBasePoints.length > 0) {
			const firstBasePointMonth = sortedBasePoints[0][0];
			const firstBasePointValue = sortedBasePoints[0][1];

			if (!acc[firstBasePointMonth]) {
				acc[firstBasePointMonth] = [];
			}

			acc[firstBasePointMonth].push({
				account_number,
				bank,
				currency,
				firstBasePointValue,
			});
		}

		return acc;
	}, {});
};

// ! ============================================================================================

export const sumBasePoints = (bankAccounts, isUSDActive, exchangeRate) => {
	const finalBasePoints = {};
	
	bankAccounts.forEach(({ basePoints, currency }) => {		
		Object.entries(basePoints).forEach(([month, value]) => {
			let amountConverted = value;

			if (isUSDActive && currency === "GTQ") {
				amountConverted = value / exchangeRate;
			} else if (!isUSDActive && currency === "USD") {
				amountConverted = value * exchangeRate;
			}

			finalBasePoints[month] = (finalBasePoints[month] || 0) + amountConverted;			
		});
	});

	return finalBasePoints;
};

// ! ============================================================================================

const processMonthlyData = (rows) => {	
	const monthlyTotals = Array(12).fill(0);
	let totalGeneral = 0;

	rows.forEach((row) => {
		if (row.total !== undefined && row.total !== null) {
			monthNames.forEach((_, index) => {
				monthlyTotals[index] += row[monthNames[index]];
			});			
			totalGeneral += row.total;
		}
	
		if (row.children && Array.isArray(row.children) && row.children.length > 0) {
			const childResult = processMonthlyData(row.children);
			childResult.monthly.forEach((value, index) => {
				monthlyTotals[index] += value;
			});
			totalGeneral += childResult.total;
		}
	});

	return {
		monthly: monthlyTotals,
		total: totalGeneral,
	};
};

// ! ============================================================================================

const getFontWeight = (depth = -1) => {
	switch (depth) {
	case 0:
		return "910";
	case 1:
		return "600";
	case 2:
		return "500";
	default:
		return "normal";
	}
};